import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Eye, Calendar, DollarSign } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface Proposta {
  id: number;
  proposal_number: string;
  title: string;
  description: string;
  total_value: string;
  status: string;
  issue_date: string;
  valid_until: string;
  file_url: string;
  notes: string;
  created_at: string;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Rascunho',
  sent: 'Enviada',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
};

export default function ClientPropostas() {
  const navigate = useNavigate();
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPropostas();
  }, []);

  const loadPropostas = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/propostas/index.php');
      
      if (!response.ok) {
        throw new Error('Erro ao carregar propostas');
      }

      const data = await response.json();
      setPropostas(data.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar propostas:', err);
      setError(err.message || 'Erro ao carregar propostas');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (proposta: Proposta) => {
    try {
      const response = await authenticatedFetch(`/api/uploads/serve.php?file=${encodeURIComponent(proposta.file_url)}`);
      
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposta_${proposta.proposal_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao baixar proposta:', err);
      alert('Erro ao baixar proposta');
    }
  };

  const handleView = async (proposta: Proposta) => {
    try {
      const response = await authenticatedFetch(`/api/uploads/serve.php?file=${encodeURIComponent(proposta.file_url)}`);
      
      if (!response.ok) {
        throw new Error('Erro ao visualizar arquivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error('Erro ao visualizar proposta:', err);
      alert('Erro ao visualizar proposta');
    }
  };

  const formatCurrency = (value: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(parseFloat(value));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/cliente/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar ao Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Propostas Comerciais</h1>
          <p className="mt-2 text-gray-600">
            Visualize e faça download das suas propostas comerciais
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Lista de Propostas */}
        {propostas.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma proposta encontrada
            </h3>
            <p className="text-gray-600">
              Você ainda não possui propostas comerciais disponíveis.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {propostas.map((proposta) => (
              <div
                key={proposta.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
              >
                {/* Header do Card */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-indigo-100 rounded-lg p-2 mr-3">
                      <FileText className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        #{proposta.proposal_number}
                      </h3>
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                          statusColors[proposta.status] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {statusLabels[proposta.status] || proposta.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Título */}
                {proposta.title && (
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    {proposta.title}
                  </h4>
                )}

                {/* Descrição */}
                {proposta.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {proposta.description}
                  </p>
                )}

                {/* Informações */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <DollarSign className="w-4 h-4 mr-2" />
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(proposta.total_value)}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>Emissão: {formatDate(proposta.issue_date)}</span>
                  </div>
                  {proposta.valid_until && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Válida até: {formatDate(proposta.valid_until)}</span>
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={() => handleView(proposta)}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Visualizar
                  </button>
                  <button
                    onClick={() => handleDownload(proposta)}
                    className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
